import { useEffect, useRef, useState } from 'react';
import { submitFeedback, type FeedbackKind } from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';

// Floating help icon — Busy mascot. Drag to reposition (saved to localStorage),
// click to open a panel with three tabs:
//   - Onboarding: feature tour with deep-links into each surface.
//   - FAQ: accordion of common questions.
//   - Feedback: submit bug/feature/question to the dev team (writes to the
//     `feedback` Supabase table per migration 0013).
//
// Mounted in AppShell so it appears on every authed route. Hidden during the
// onboarding flow itself + on the auth/landing pages (not wrapped by AppShell).

const STORAGE_KEY = 'busy.helper.pos';
const ICON_SIZE = 60;
const DRAG_THRESHOLD = 6; // px before we count a press as a drag, not a click
const APP_VERSION = 'v1.0.0';

type Pos = { left: number; top: number };

function defaultPos(): Pos {
  // Default: lower-right, above the bottom tab bar.
  if (typeof window === 'undefined') return { left: 0, top: 0 };
  return {
    left: window.innerWidth - ICON_SIZE - 16,
    top: window.innerHeight - ICON_SIZE - 96,
  };
}

function loadPos(): Pos {
  if (typeof window === 'undefined') return { left: 0, top: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPos();
    const parsed = JSON.parse(raw) as Pos;
    return clampToViewport(parsed);
  } catch {
    return defaultPos();
  }
}

function clampToViewport(pos: Pos): Pos {
  if (typeof window === 'undefined') return pos;
  const maxLeft = window.innerWidth - ICON_SIZE;
  const maxTop = window.innerHeight - ICON_SIZE;
  return {
    left: Math.max(0, Math.min(pos.left, maxLeft)),
    top: Math.max(0, Math.min(pos.top, maxTop)),
  };
}

export function BusyHelper() {
  const t = useT();
  const visible = useUI((s) => s.helperVisible);
  const [pos, setPos] = useState<Pos>(() => defaultPos());
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragState = useRef<{
    pointerStart: Pos;
    iconStart: Pos;
    moved: boolean;
  } | null>(null);

  // Hydrate from localStorage on mount (window-safe).
  useEffect(() => {
    setPos(loadPos());
  }, []);

  // Re-clamp on viewport resize so the icon doesn't end up off-screen on rotate.
  useEffect(() => {
    function onResize() {
      setPos((p) => clampToViewport(p));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    buttonRef.current?.setPointerCapture(e.pointerId);
    dragState.current = {
      pointerStart: { left: e.clientX, top: e.clientY },
      iconStart: pos,
      moved: false,
    };
  }

  function onPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const s = dragState.current;
    if (!s) return;
    const dx = e.clientX - s.pointerStart.left;
    const dy = e.clientY - s.pointerStart.top;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    s.moved = true;
    if (!dragging) setDragging(true);
    setPos(clampToViewport({ left: s.iconStart.left + dx, top: s.iconStart.top + dy }));
  }

  function onPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const s = dragState.current;
    dragState.current = null;
    setDragging(false);
    buttonRef.current?.releasePointerCapture(e.pointerId);
    if (!s) return;
    if (s.moved) {
      // Persist new position
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } catch {
        /* ignore quota errors */
      }
    } else {
      setOpen(true);
    }
  }

  // Visibility gate — experienced users can hide the mascot from Settings.
  // Returning null also tears down the drag listeners cleanly.
  if (!visible) {
    return open ? <HelperPanel onClose={() => setOpen(false)} /> : null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('helper.aria')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          dragState.current = null;
          setDragging(false);
        }}
        className={`fixed z-40 rounded-full bg-navy-900 shadow-lg border-2 border-gold-500 grid place-items-center select-none touch-none ${
          dragging ? 'cursor-grabbing scale-105' : 'cursor-grab hover:scale-105'
        } transition-transform`}
        style={{
          left: pos.left,
          top: pos.top,
          width: ICON_SIZE,
          height: ICON_SIZE,
        }}
      >
        <img
          src="/assets/busy-mascot.png"
          alt=""
          draggable={false}
          className="w-[88%] h-[88%] object-contain pointer-events-none"
        />
      </button>

      {open && <HelperPanel onClose={() => setOpen(false)} />}
    </>
  );
}

/* ============================================================================
 * Panel
 * ============================================================================ */

type Tab = 'guide' | 'faq' | 'feedback';

function HelperPanel({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>('guide');
  const setHelperVisible = useUI((s) => s.setHelperVisible);

  function handleHide() {
    setHelperVisible(false);
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('helper.aria')}>
      <div className="bg-navy-900 text-white px-6 pt-6 pb-4 rounded-t-2xl relative">
        <ModalCloseButton onClose={onClose} variant="dark" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full border-2 border-gold-500 bg-navy-800 grid place-items-center overflow-hidden">
            <img src="/assets/busy-mascot.png" alt="" className="w-[88%] h-[88%] object-contain" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-gold-400">
              {t('helper.eyebrow')}
            </div>
            <h2 className="font-display text-2xl leading-tight">{t('helper.title')}</h2>
          </div>
        </div>

        <div className="flex gap-1 mt-5 bg-white/8 rounded-lg p-1">
          <TabButton active={tab === 'guide'} onClick={() => setTab('guide')} label={t('helper.tab.guide')} />
          <TabButton active={tab === 'faq'} onClick={() => setTab('faq')} label={t('helper.tab.faq')} />
          <TabButton active={tab === 'feedback'} onClick={() => setTab('feedback')} label={t('helper.tab.feedback')} />
        </div>
      </div>

      <div className="p-5 max-h-[60dvh] overflow-y-auto">
        {tab === 'guide' && <GuideTab onClose={onClose} />}
        {tab === 'faq' && <FaqTab />}
        {tab === 'feedback' && <FeedbackTab onSent={onClose} />}
      </div>

      {/* Footer — hide-Busy escape hatch. Restorable from Settings → Appearance. */}
      <div className="border-t border-line bg-bg-alt px-5 py-2.5 text-center">
        <button
          type="button"
          onClick={handleHide}
          className="text-[11px] font-semibold text-ink-500 hover:text-ink-700 underline-offset-2 hover:underline"
        >
          {t('helper.hide.cta')}
        </button>
      </div>
    </ModalShell>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 h-9 rounded-md text-xs font-semibold transition-colors ${
        active ? 'bg-gold-500 text-navy-900' : 'text-white/80 hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

/* ============================================================================
 * Onboarding guide tab — master/detail with swipe slideshows.
 *
 * Tapping a topic opens a horizontal-swipe carousel of slides specific to
 * that topic (HelperSlideshow). First topic is a "Getting Started" tour
 * for brand-new barbers; the rest cover each surface of the app. Slides
 * support optional screenshots via /assets/help/<filename>.png — drop a
 * capture at that path and the placeholder swaps to the real screenshot
 * with no code change.
 * ============================================================================ */

import { HelperSlideshow, type HelperSlide } from './helper/HelperSlideshow';

type GuideKey =
  | 'getting-started'
  | 'today'
  | 'lessons'
  | 'cuttimer'
  | 'clients'
  | 'dashboard'
  | 'achievements'
  | 'settings';

type GuideStep = {
  key: GuideKey;
  icon: HelperIconName;
  slides: HelperSlide[];
};

const GUIDE_STEPS: readonly GuideStep[] = [
  // ============== Getting Started ==============
  {
    key: 'getting-started',
    icon: 'spark',
    slides: [
      {
        titleKey: 'helper.gs.welcome.title',
        bodyKey:  'helper.gs.welcome.body',
        image: 'today-screen.png',
      },
      {
        titleKey: 'helper.gs.daily.title',
        bodyKey:  'helper.gs.daily.body',
        bulletKeys: [
          'helper.gs.daily.bullet1',
          'helper.gs.daily.bullet2',
          'helper.gs.daily.bullet3',
        ],
        image: 'daily-lesson.png',
      },
      {
        titleKey: 'helper.gs.clients.title',
        bodyKey:  'helper.gs.clients.body',
        bulletKeys: [
          'helper.gs.clients.bullet1',
          'helper.gs.clients.bullet2',
          'helper.gs.clients.bullet3',
        ],
        image: 'clients-roster.png',
      },
      {
        titleKey: 'helper.gs.cut.title',
        bodyKey:  'helper.gs.cut.body',
        bulletKeys: [
          'helper.gs.cut.bullet1',
          'helper.gs.cut.bullet2',
          'helper.gs.cut.bullet3',
        ],
        image: 'cut-timer-live.png',
      },
      {
        titleKey: 'helper.gs.progress.title',
        bodyKey:  'helper.gs.progress.body',
        tipKey: 'helper.gs.progress.tip',
        image: 'dashboard-overview.png',
        ctaRoute: '/today',
      },
    ],
  },

  // ============== Today ==============
  {
    key: 'today',
    icon: 'sun',
    slides: [
      {
        titleKey: 'helper.t.lesson.title',
        bodyKey:  'helper.t.lesson.body',
        bulletKeys: ['helper.t.lesson.bullet1', 'helper.t.lesson.bullet2'],
        image: 'daily-lesson.png',
      },
      {
        titleKey: 'helper.t.streak.title',
        bodyKey:  'helper.t.streak.body',
        bulletKeys: ['helper.t.streak.bullet1', 'helper.t.streak.bullet2', 'helper.t.streak.bullet3'],
        image: 'streak-ribbon.png',
      },
      {
        titleKey: 'helper.t.almost.title',
        bodyKey:  'helper.t.almost.body',
        image: 'almost-there.png',
      },
      {
        titleKey: 'helper.t.eod.title',
        bodyKey:  'helper.t.eod.body',
        tipKey: 'helper.t.eod.tip',
        image: 'eod-wrapup.png',
        ctaRoute: '/today',
      },
    ],
  },

  // ============== Lessons ==============
  {
    key: 'lessons',
    icon: 'book',
    slides: [
      {
        titleKey: 'helper.l.drip.title',
        bodyKey:  'helper.l.drip.body',
        image: 'lessons-tab.png',
      },
      {
        titleKey: 'helper.l.locked.title',
        bodyKey:  'helper.l.locked.body',
        image: 'lessons-locked.png',
      },
      {
        titleKey: 'helper.l.bonus.title',
        bodyKey:  'helper.l.bonus.body',
        bulletKeys: ['helper.l.bonus.bullet1', 'helper.l.bonus.bullet2'],
        image: 'lesson-bonus.png',
      },
      {
        titleKey: 'helper.l.poll.title',
        bodyKey:  'helper.l.poll.body',
        tipKey: 'helper.l.poll.tip',
        image: 'helpful-poll.png',
        ctaRoute: '/lessons',
      },
    ],
  },

  // ============== Cut Timer ==============
  {
    key: 'cuttimer',
    icon: 'scissors',
    slides: [
      {
        titleKey: 'helper.c.start.title',
        bodyKey:  'helper.c.start.body',
        image: 'cut-timer-pick.png',
      },
      {
        titleKey: 'helper.c.live.title',
        bodyKey:  'helper.c.live.body',
        bulletKeys: ['helper.c.live.bullet1', 'helper.c.live.bullet2', 'helper.c.live.bullet3'],
        image: 'cut-timer-live.png',
      },
      {
        titleKey: 'helper.c.tips.title',
        bodyKey:  'helper.c.tips.body',
        bulletKeys: ['helper.c.tips.bullet1', 'helper.c.tips.bullet2'],
        image: 'cut-timer-tips.png',
      },
      {
        titleKey: 'helper.c.transform.title',
        bodyKey:  'helper.c.transform.body',
        bulletKeys: [
          'helper.c.transform.bullet1',
          'helper.c.transform.bullet2',
          'helper.c.transform.bullet3',
        ],
        image: 'cut-timer-transformation.png',
      },
      {
        titleKey: 'helper.c.save.title',
        bodyKey:  'helper.c.save.body',
        tipKey: 'helper.c.save.tip',
        image: 'cut-timer-summary.png',
        ctaRoute: '/today',
      },
    ],
  },

  // ============== Clients ==============
  {
    key: 'clients',
    icon: 'users',
    slides: [
      {
        titleKey: 'helper.cl.roster.title',
        bodyKey:  'helper.cl.roster.body',
        bulletKeys: ['helper.cl.roster.bullet1', 'helper.cl.roster.bullet2'],
        image: 'clients-roster.png',
      },
      {
        titleKey: 'helper.cl.add.title',
        bodyKey:  'helper.cl.add.body',
        bulletKeys: ['helper.cl.add.bullet1', 'helper.cl.add.bullet2'],
        image: 'client-add.png',
      },
      {
        titleKey: 'helper.cl.detail.title',
        bodyKey:  'helper.cl.detail.body',
        bulletKeys: ['helper.cl.detail.bullet1', 'helper.cl.detail.bullet2', 'helper.cl.detail.bullet3'],
        image: 'client-detail.png',
      },
      {
        titleKey: 'helper.cl.archive.title',
        bodyKey:  'helper.cl.archive.body',
        tipKey: 'helper.cl.archive.tip',
        image: 'client-archive.png',
        ctaRoute: '/clients',
      },
    ],
  },

  // ============== Dashboard ==============
  {
    key: 'dashboard',
    icon: 'chart',
    slides: [
      {
        titleKey: 'helper.d.metrics.title',
        bodyKey:  'helper.d.metrics.body',
        image: 'dashboard-overview.png',
      },
      {
        titleKey: 'helper.d.tiers.title',
        bodyKey:  'helper.d.tiers.body',
        tipKey: 'helper.d.tiers.tip',
        image: 'dashboard-tiers.png',
      },
      {
        titleKey: 'helper.d.recaps.title',
        bodyKey:  'helper.d.recaps.body',
        bulletKeys: ['helper.d.recaps.bullet1', 'helper.d.recaps.bullet2'],
        image: 'recap-modal.png',
        ctaRoute: '/dashboard',
      },
    ],
  },

  // ============== Achievements ==============
  {
    key: 'achievements',
    icon: 'trophy',
    slides: [
      {
        titleKey: 'helper.a.tiers.title',
        bodyKey:  'helper.a.tiers.body',
        image: 'achievements-gallery.png',
      },
      {
        titleKey: 'helper.a.celebrate.title',
        bodyKey:  'helper.a.celebrate.body',
        bulletKeys: [
          'helper.a.celebrate.bullet1',
          'helper.a.celebrate.bullet2',
          'helper.a.celebrate.bullet3',
          'helper.a.celebrate.bullet4',
          'helper.a.celebrate.bullet5',
        ],
        image: 'celebration-modal.png',
      },
      {
        titleKey: 'helper.a.nicknames.title',
        bodyKey:  'helper.a.nicknames.body',
        tipKey: 'helper.a.nicknames.tip',
        image: 'achievements-nickname.png',
        ctaRoute: '/achievements',
      },
    ],
  },

  // ============== Settings ==============
  {
    key: 'settings',
    icon: 'cog',
    slides: [
      {
        titleKey: 'helper.s.profile.title',
        bodyKey:  'helper.s.profile.body',
        image: 'settings-profile.png',
      },
      {
        titleKey: 'helper.s.cuttimer.title',
        bodyKey:  'helper.s.cuttimer.body',
        bulletKeys: ['helper.s.cuttimer.bullet1', 'helper.s.cuttimer.bullet2'],
        image: 'settings-cuttimer.png',
      },
      {
        titleKey: 'helper.s.delivery.title',
        bodyKey:  'helper.s.delivery.body',
        image: 'settings-delivery.png',
      },
      {
        titleKey: 'helper.s.privacy.title',
        bodyKey:  'helper.s.privacy.body',
        tipKey: 'helper.s.privacy.tip',
        image: 'settings-privacy.png',
        ctaRoute: '/settings',
      },
    ],
  },
];

function GuideTab({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [selected, setSelected] = useState<GuideKey | null>(null);

  if (selected) {
    const step = GUIDE_STEPS.find((s) => s.key === selected);
    if (!step) return null;
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 hover:text-ink-700"
        >
          <HelperIcon name="chevron-left" className="w-3.5 h-3.5" />
          {t('helper.guide.back')}
        </button>
        <header className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center shrink-0 text-navy-900 dark:text-gold-400">
            <HelperIcon name={step.icon} className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-xl leading-tight">{t(`helper.guide.${step.key}.title`)}</h3>
            <p className="text-xs text-ink-500">{t('helper.slide.swipeHint')}</p>
          </div>
        </header>
        <HelperSlideshow slides={step.slides} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-500 leading-relaxed">{t('helper.guide.intro')}</p>
      <ol className="space-y-2">
        {GUIDE_STEPS.map((step) => {
          const isStarter = step.key === 'getting-started';
          return (
            <li key={step.key}>
              <button
                type="button"
                onClick={() => setSelected(step.key)}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${
                  isStarter
                    ? 'border-gold-500 bg-gold-500/10 hover:bg-gold-500/15'
                    : 'border-line bg-surface hover:border-navy-900/40 dark:hover:border-gold-500/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                    isStarter
                      ? 'bg-gold-500 text-navy-900'
                      : 'bg-navy-900/8 dark:bg-gold-500/15 text-navy-900 dark:text-gold-400'
                  }`}>
                    <HelperIcon name={step.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <strong className="block text-[15px]">{t(`helper.guide.${step.key}.title`)}</strong>
                    <span className="text-xs text-ink-500 leading-relaxed">
                      {t(`helper.guide.${step.key}.body`)}
                    </span>
                  </div>
                  <HelperIcon name="chevron-right" className="w-4 h-4 text-ink-500 mt-1.5 shrink-0" />
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ============================================================================
 * FAQ tab
 * ============================================================================ */

// Order intentional: most-recently-shipped features at the top so a
// barber opening the FAQ after a fresh update lands on what changed.
// Original entries (streak / tier / achievements / etc.) below.
const FAQ_KEYS: string[] = [
  // Most-recently-shipped flows at the top so the FAQ opens onto
  // what changed for a barber returning after an update.
  'cutFlow',
  'transformation',
  'earningRate',
  'cutEdit',
  'chartWeather',
  'capacityChips',
  'lessonsBookshelf',
  'recapShare',
  'menuShare',
  'streak',
  'tier',
  'achievements',
  'csv',
  'language',
  'photos',
  'data',
  'platforms',
];

function FaqTab() {
  const t = useT();
  // Start with everything collapsed so the user scans the question list
  // first and taps to expand whichever they care about.
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <div className="space-y-2">
      {FAQ_KEYS.map((key, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={key} className="rounded-xl border border-line bg-surface">
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? null : i)}
              className="w-full text-left p-3 flex items-start gap-3"
            >
              <strong className="flex-1 text-sm">{t(`helper.faq.${key}.q`)}</strong>
              <span className="text-ink-500 text-xs mt-0.5">{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
              <div className="px-3 pb-3 text-[13px] text-ink-700 leading-relaxed">
                {t(`helper.faq.${key}.a`)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================================
 * Feedback tab
 * ============================================================================ */

function FeedbackTab({ onSent }: { onSent: () => void }) {
  const t = useT();
  const toast = useToast();
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    const trimmed = body.trim();
    if (trimmed.length < 5) {
      toast(t('helper.feedback.tooShort'), 'error');
      return;
    }
    setSubmitting(true);
    const { error } = await submitFeedback({
      kind,
      body: trimmed,
      url: typeof window !== 'undefined' ? window.location.pathname : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      appVersion: APP_VERSION,
    });
    setSubmitting(false);
    if (error) {
      toast(t('helper.feedback.error', { message: error.message }), 'error');
      return;
    }
    setBody('');
    toast(t('helper.feedback.thanks'), 'success');
    onSent();
  }

  const KINDS: Array<{ id: FeedbackKind; labelKey: string; icon: HelperIconName }> = [
    { id: 'bug',      labelKey: 'helper.feedback.kind.bug',      icon: 'bug' },
    { id: 'feature',  labelKey: 'helper.feedback.kind.feature',  icon: 'lightbulb' },
    { id: 'question', labelKey: 'helper.feedback.kind.question', icon: 'help' },
    { id: 'other',    labelKey: 'helper.feedback.kind.other',    icon: 'message' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-500 leading-relaxed">{t('helper.feedback.intro')}</p>

      <div>
        <label className="block text-xs font-bold text-ink-500 mb-1.5">
          {t('helper.feedback.kindLabel')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => setKind(k.id)}
              className={`h-10 rounded-lg border text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                kind === k.id
                  ? 'border-navy-900 dark:border-gold-500 bg-navy-900/5 dark:bg-gold-500/10'
                  : 'border-line bg-surface hover:border-navy-900/40 dark:hover:border-gold-500/40'
              }`}
            >
              <HelperIcon name={k.icon} className="w-4 h-4" />
              <span>{t(k.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-ink-500 mb-1.5">
          {t('helper.feedback.bodyLabel')}
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder={t('helper.feedback.bodyPlaceholder')}
          className="w-full p-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 resize-y focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
        />
      </div>

      <p className="text-[11px] text-ink-500 leading-snug">
        {t('helper.feedback.privacyNote')}
      </p>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || body.trim().length < 5}
        className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
      >
        {submitting ? t('helper.feedback.sending') : t('helper.feedback.send')}
      </button>
    </div>
  );
}

/* ============================================================================
 * Inline SVG icons. Lucide-style 24×24 viewBox with currentColor strokes so
 * they inherit the surrounding text color and theme cleanly.
 * ============================================================================ */

type HelperIconName =
  | 'sun' | 'book' | 'scissors' | 'users' | 'chart' | 'trophy' | 'cog'
  | 'check' | 'lightbulb' | 'arrow-right' | 'chevron-right' | 'chevron-left'
  | 'bug' | 'help' | 'message' | 'spark';

function HelperIcon({ name, className }: { name: HelperIconName; className?: string }) {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'sun':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    case 'book':
      return (
        <svg {...props}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'scissors':
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <line x1="20" y1="4" x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...props}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
          <line x1="3" y1="20" x2="21" y2="20" />
        </svg>
      );
    case 'trophy':
      return (
        <svg {...props}>
          <path d="M8 21h8M12 17v4" />
          <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
          <path d="M17 4h3v3a3 3 0 0 1-3 3M7 4H4v3a3 3 0 0 0 3 3" />
        </svg>
      );
    case 'cog':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'lightbulb':
      return (
        <svg {...props}>
          <path d="M9 18h6M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
        </svg>
      );
    case 'arrow-right':
      return (
        <svg {...props}>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...props}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      );
    case 'chevron-left':
      return (
        <svg {...props}>
          <polyline points="15 18 9 12 15 6" />
        </svg>
      );
    case 'bug':
      return (
        <svg {...props}>
          <rect x="8" y="6" width="8" height="14" rx="4" />
          <path d="M12 2v4M9 4l3 2 3-2" />
          <path d="M3 13h5M16 13h5M3 9l4 1M21 9l-4 1M3 17l4-1M21 17l-4-1" />
        </svg>
      );
    case 'help':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'message':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...props}>
          <path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M5 19l4-4M15 9l4-4" />
        </svg>
      );
  }
}
